import { Close, EditOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { updateProfile } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

const BRAND = '#0c5bab';
const NAME_REGEX = /^[A-Za-z][A-Za-z' -]*$/;
const PHONE_REGEX = /^[0-9]{11}$/;

const toForm = (consultant) => ({
  firstname: consultant?.firstname || '',
  lastname: consultant?.lastname || '',
  phone: consultant?.phone || '',
  email: consultant?.email || '',
});

const validate = ({ firstname, lastname, phone }) => {
  const errors = {};
  if (!firstname.trim()) errors.firstname = 'First name is required';
  else if (!NAME_REGEX.test(firstname.trim())) errors.firstname = 'Use letters only';
  if (!lastname.trim()) errors.lastname = 'Last name is required';
  else if (!NAME_REGEX.test(lastname.trim())) errors.lastname = 'Use letters only';
  if (phone && !PHONE_REGEX.test(phone.trim())) errors.phone = 'Phone number should be 11 digits';
  return errors;
};

export default function EditConsultant({ open, onClose, consultant }) {
  const [form, setForm] = useState(toForm(consultant));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(consultant));
      setErrors({});
    }
  }, [open, consultant]);

  const original = toForm(consultant);
  const hasChanges = ['firstname', 'lastname', 'phone'].some(
    (key) => form[key].trim() !== original[key].trim()
  );

  const handleChange = (key) => (event) => {
    const value = key === 'phone' ? event.target.value.replace(/\D/g, '') : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    setSaving(true);
    try {
      await updateProfile(
        {
          firstname: form.firstname.trim(),
          lastname: form.lastname.trim(),
          phone: form.phone.trim(),
          email: form.email,
        },
        consultant?.userId
      );
      openNotification({ type: 'success', message: 'Consultant details updated.' });
      onClose();
    } catch (error) {
      const errmsg =
        error?.data?.result?.status?.desc ||
        error?.result?.status?.desc ||
        (typeof error === 'string' ? error : '') ||
        'Unable to update consultant details.';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ component: 'form', onSubmit: handleSubmit, sx: { borderRadius: '16px' } }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(BRAND, 0.08),
            color: BRAND,
            flexShrink: 0,
          }}
        >
          <EditOutlined />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
            Edit consultant
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            Update {consultant?.firstname || 'this consultant'}'s contact details
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close">
          <Close />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pt: '16px !important' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <TextField
            label="First name"
            value={form.firstname}
            onChange={handleChange('firstname')}
            error={Boolean(errors.firstname)}
            helperText={errors.firstname}
            required
            autoFocus
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            label="Last name"
            value={form.lastname}
            onChange={handleChange('lastname')}
            error={Boolean(errors.lastname)}
            helperText={errors.lastname}
            required
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            label="Phone number"
            value={form.phone}
            onChange={handleChange('phone')}
            error={Boolean(errors.phone)}
            helperText={errors.phone || '11 digits, e.g. 08012345678'}
            fullWidth
            inputProps={{ inputMode: 'numeric', maxLength: 11 }}
          />
          <TextField
            label="Email address"
            value={form.email}
            fullWidth
            disabled
            helperText="Email address can't be changed"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit" sx={{ borderRadius: '8px' }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={saving || !hasChanges}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: '8px', px: 3 }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
